import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { api } from '../api';
import { PrintDocumentButton } from '../components/PrintDocument';
import { ErrorMessage, Spinner, number, when } from '../ui';

interface Weight {
  orderedG: number;
  outstandingG: number;
  piecesWithoutWeight: number;
  byPurchase: Record<string, { orderedG: number; outstandingG: number; piecesWithoutWeight: number }>;
}
interface Shipment {
  id: number;
  reference: string;
  status: string;
  carrier: string | null;
  trackingNo: string | null;
  expectedShipOn: string | null;
  actualShipOn: string | null;
  expectedReceiptOn: string;
  actualReceiptOn: string | null;
  notes: string | null;
  purchases: { id: number; description: string; status: string; piecesOrdered: number; piecesReceived: number; piecesOutstanding: number }[];
  weight: Weight;
}

/** Grams under a kilo, kilograms to two places above it. */
const grams = (g: number) => (g >= 1000 ? `${(g / 1000).toFixed(2)} kg` : `${Math.round(g)} g`);

/** A printable manifest of an inbound shipment: what it is, what is in it, and roughly what the parcel weighs. */
export function InboundShipmentDocument() {
  const { id } = useParams();
  const q = useQuery({ queryKey: ['inbound-shipment-document', id], queryFn: () => api<{ shipment: Shipment }>(`/api/v1/inbound-shipments/${id}`).then((r) => r.shipment) });
  if (q.isPending) return <Spinner />;
  if (q.error || !q.data) return <ErrorMessage error={q.error} />;
  const s = q.data;
  const pieces = s.purchases.reduce((n, p) => n + p.piecesOrdered, 0);
  const received = s.purchases.reduce((n, p) => n + p.piecesReceived, 0);
  const detail = (label: string, value: string | null) => (
    <div>
      <strong>{label}</strong>
      <div>{value || '—'}</div>
    </div>
  );
  return (
    <main data-document className="mx-auto max-w-3xl bg-white p-8 print:max-w-none print:p-0">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">BrickLoop</h1>
          <h2 className="mt-2 text-xl">Inbound Shipment</h2>
          <p className="text-sm text-slate-600">
            {s.reference} · printed {when(new Date().toISOString())}
          </p>
        </div>
        <PrintDocumentButton recordType="shipment" recordId={s.id} kind="manifest" />
      </div>

      <div className="mb-6 grid gap-3 text-sm sm:grid-cols-3">
        {detail('Status', s.status === 'arrived' ? 'Arrived, to be counted' : s.status)}
        {detail('Carrier', s.carrier)}
        {detail('Tracking number', s.trackingNo)}
        {detail('Expected dispatch', s.expectedShipOn)}
        {detail('Actual dispatch', s.actualShipOn)}
        <span />
        {detail('Expected receipt', s.expectedReceiptOn)}
        {detail('Actual receipt', s.actualReceiptOn)}
      </div>

      <div className="mb-6 rounded-md border border-slate-300 p-4">
        <div className="text-sm text-slate-600">Estimated parcel weight</div>
        <div className="text-3xl font-bold tabular-nums">{grams(s.weight.orderedG)}</div>
        <p className="mt-1 text-sm text-slate-600">
          Pieces ordered times each item's catalogue weight, for {number(pieces)} piece{pieces === 1 ? '' : 's'}.
          {s.weight.outstandingG !== s.weight.orderedG && <> Still to arrive: {grams(s.weight.outstandingG)}.</>}
          {s.weight.piecesWithoutWeight > 0 && (
            <>
              {' '}
              <strong>{number(s.weight.piecesWithoutWeight)} piece{s.weight.piecesWithoutWeight === 1 ? ' has' : 's have'} no weight on record and {s.weight.piecesWithoutWeight === 1 ? 'is' : 'are'} not counted, so the parcel is at least this heavy.</strong>
            </>
          )}{' '}
          Packaging is not included.
        </p>
      </div>

      <table className="data w-full text-sm">
        <thead>
          <tr>
            <th>Purchase order</th>
            <th className="text-right">Ordered</th>
            <th className="text-right">Received</th>
            <th className="text-right">Outstanding</th>
            <th className="text-right">Est. weight</th>
          </tr>
        </thead>
        <tbody>
          {s.purchases.map((p) => {
            const w = s.weight.byPurchase[String(p.id)];
            return (
              <tr key={p.id}>
                <td>
                  <strong>Purchase {p.id}</strong>
                  <div className="text-xs text-slate-500">{p.description} · {p.status.replace('_', ' ')}</div>
                </td>
                <td className="text-right tabular-nums">{number(p.piecesOrdered)}</td>
                <td className="text-right tabular-nums">{number(p.piecesReceived)}</td>
                <td className="text-right tabular-nums">{number(p.piecesOutstanding)}</td>
                <td className="text-right tabular-nums">
                  {w ? grams(w.orderedG) : '—'}
                  {w && w.piecesWithoutWeight > 0 && <div className="text-xs text-slate-500">{number(w.piecesWithoutWeight)} unweighed</div>}
                </td>
              </tr>
            );
          })}
          <tr className="font-semibold">
            <td>Total</td>
            <td className="text-right tabular-nums">{number(pieces)}</td>
            <td className="text-right tabular-nums">{number(received)}</td>
            <td className="text-right tabular-nums">{number(pieces - received)}</td>
            <td className="text-right tabular-nums">{grams(s.weight.orderedG)}</td>
          </tr>
        </tbody>
      </table>
      {s.notes && (
        <p className="mt-6 whitespace-pre-wrap text-sm">
          <strong>Notes</strong>
          <br />
          {s.notes}
        </p>
      )}
    </main>
  );
}
